import React, { useCallback, useMemo, useState, createElement, Component } from 'react';
import { Play, CheckCircle2, AlertTriangle, AlertOctagon, Info, ChevronDown, ChevronRight, RefreshCw, Download, Shield, Eye, BookOpen, Layers, Zap } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Question } from '../../../types';
import { runPipeline, runPipelineBatch } from '../../../governance/pipeline/orchestrator';
import type { PipelineResult, ValidationIssue, IssueKind } from '../../../governance/pipeline/issueContract';
interface ValidationRunPanelProps {
  questions: Question[];
}
type DomainFilter = 'all' | ValidationIssue['domain'];
type SeverityFilter = 'all' | 'blocker' | 'coach' | 'info';
const DOMAIN_META: Record<ValidationIssue['domain'], {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  color: string;
}> = {
  schema: {
    label: 'Schema',
    icon: Layers,
    color: 'text-slate-600'
  },
  type: {
    label: 'Type',
    icon: Layers,
    color: 'text-slate-500'
  },
  graph: {
    label: 'Graph',
    icon: BookOpen,
    color: 'text-blue-600'
  },
  content: {
    label: 'Content',
    icon: Shield,
    color: 'text-purple-600'
  },
  visual: {
    label: 'Visual',
    icon: Eye,
    color: 'text-emerald-600'
  },
  support: {
    label: 'Support',
    icon: Zap,
    color: 'text-amber-600'
  },
  scoring: {
    label: 'Scoring',
    icon: AlertOctagon,
    color: 'text-indigo-600'
  },
  gate: {
    label: 'Gate',
    icon: AlertOctagon,
    color: 'text-rose-600'
  }
};
export function ValidationRunPanel({
  questions
}: ValidationRunPanelProps) {
  const [hasRun, setHasRun] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [batchResult, setBatchResult] = useState<ReturnType<typeof runPipelineBatch> | null>(null);
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showOnlyFailing, setShowOnlyFailing] = useState(true);
  const runValidation = useCallback(async () => {
    setIsRunning(true);
    setHasRun(false);
    await new Promise(r => setTimeout(r, 50));
    const result = runPipelineBatch(questions);
    setBatchResult(result);
    setHasRun(true);
    setIsRunning(false);
  }, [questions]);
  const summary = useMemo(() => {
    if (!batchResult) return null;
    const allIssues = batchResult.results.flatMap(r => r.gate.issues);
    const blockers = allIssues.filter(i => i.kind === 'blocker').length;
    const coaches = allIssues.filter(i => i.kind === 'coach').length;
    const infos = allIssues.filter(i => i.kind === 'info').length;
    const passing = batchResult.results.filter(r => r.gate.verdict === 'PASS').length;
    const byDomain: Record<string, number> = {};
    allIssues.forEach(i => {
      byDomain[i.domain] = (byDomain[i.domain] ?? 0) + 1;
    });
    const scoreDistribution = {
      excellent: batchResult.results.filter(r => r.gate.score >= 90).length,
      good: batchResult.results.filter(r => r.gate.score >= 70 && r.gate.score < 90).length,
      needsWork: batchResult.results.filter(r => r.gate.score >= 50 && r.gate.score < 70).length,
      poor: batchResult.results.filter(r => r.gate.score < 50).length
    };
    return {
      blockers,
      coaches,
      infos,
      passing,
      avgScore: batchResult.avgScore,
      byDomain,
      scoreDistribution,
      total: batchResult.totalQuestions,
      productionEligible: batchResult.productionEligible,
      assessmentEligible: batchResult.assessmentEligible
    };
  }, [batchResult]);
  const filteredResults = useMemo(() => {
    if (!batchResult) return [];
    return batchResult.results.filter(r => {
      if (showOnlyFailing && r.gate.verdict === 'PASS' && r.gate.issues.length === 0) return false;
      const issues = domainFilter === 'all' ? r.gate.issues : r.gate.issues.filter(i => i.domain === domainFilter);
      const filtered = severityFilter === 'all' ? issues : issues.filter(i => i.kind === severityFilter);
      return filtered.length > 0 || !showOnlyFailing;
    }).sort((a, b) => a.gate.score - b.gate.score);
  }, [batchResult, domainFilter, severityFilter, showOnlyFailing]);
  const exportResults = () => {
    if (!batchResult) return;
    const rows = batchResult.results.flatMap(r => r.gate.issues.map(i => ({
      questionId: r.questionId,
      familyId: r.familyId,
      score: r.gate.score,
      verdict: r.gate.verdict,
      domain: i.domain,
      kind: i.kind,
      code: i.code,
      message: i.message
    })));
    const csv = ['questionId,familyId,score,verdict,domain,kind,code,message', ...rows.map(r => [r.questionId, r.familyId, r.score, r.verdict, r.domain, r.kind, r.code, `"${r.message.replace(/"/g, '""')}"`].join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-run-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-3134">
      {/* Run Header */}
      <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0" data-id="element-3135">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3136">
          <div data-id="element-3137">
            <h2 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-3138">
              <Shield className="w-5 h-5 text-indigo-400" data-id="element-3139" />
              Full Validation Run
            </h2>
            <p className="text-slate-400 text-sm mt-1" data-id="element-3140">
              Pipeline v4.0 — Schema · Type · Graph · Content · Visual · Support
              · Gate
            </p>
            <p className="text-slate-500 text-xs mt-1" data-id="element-3141">
              {questions.length} questions queued · Strict mode active
            </p>
          </div>
          <div className="flex gap-3" data-id="element-3142">
            {hasRun && <Button onClick={exportResults} variant="secondary" size="md" className="bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600" data-id="element-3143">
                <Download className="w-4 h-4 mr-2" data-id="element-3144" />
                Export CSV
              </Button>}
            <Button onClick={runValidation} disabled={isRunning} size="md" className="bg-indigo-600 hover:bg-indigo-500 border-0 text-white shadow-lg shadow-indigo-900/40 min-w-[160px]" data-id="element-3145">
              {isRunning ? <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-3146" />
                  Validating…
                </> : <>
                  <Play className="w-4 h-4 mr-2 fill-current" data-id="element-3147" />
                  {hasRun ? 'Re-run Validation' : 'Run Full Validation'}
                </>}
            </Button>
          </div>
        </div>
      </Card>

      {/* Pre-run state */}
      {!hasRun && !isRunning && <Card className="p-12 text-center border-dashed border-2 border-slate-200 bg-slate-50/50" data-id="element-3148">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-3149">
            <Shield className="w-8 h-8 text-slate-400" data-id="element-3150" />
          </div>
          <h3 className="text-lg font-bold text-slate-700 mb-2" data-id="element-3151">
            Ready to validate
          </h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto" data-id="element-3152">
            Click "Run Full Validation" to execute the full pipeline across{' '}
            {questions.length} questions. Strict mode is active — all feature
            flags enforced.
          </p>
        </Card>}

      {isRunning && <Card className="p-12 text-center" data-id="element-3153">
          <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" data-id="element-3154" />
          <p className="text-slate-600 font-semibold" data-id="element-3155">
            Running validation pipeline…
          </p>
          <p className="text-slate-400 text-sm mt-1" data-id="element-3156">
            Analyzing {questions.length} questions
          </p>
        </Card>}

      {/* Results */}
      {hasRun && summary && batchResult && <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3157">
            <SummaryCard label="Avg Score" value={`${summary.avgScore}`} suffix="/100" color={summary.avgScore >= 80 ? 'emerald' : summary.avgScore >= 60 ? 'amber' : 'rose'} icon={CheckCircle2} data-id="element-3158" />
            <SummaryCard label="Passing" value={`${summary.passing}`} suffix={`/ ${summary.total}`} color="emerald" icon={CheckCircle2} data-id="element-3159" />
            <SummaryCard label="Blockers" value={`${summary.blockers}`} color={summary.blockers === 0 ? 'emerald' : 'rose'} icon={AlertOctagon} data-id="element-3160" />
            <SummaryCard label="Coach Issues" value={`${summary.coaches}`} color={summary.coaches === 0 ? 'emerald' : 'amber'} icon={AlertTriangle} data-id="element-3161" />
          </div>

          {/* Eligibility Row */}
          <div className="grid grid-cols-2 gap-4" data-id="element-3162">
            <Card className="p-4 flex items-center gap-3 border-slate-200" data-id="element-3163">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${summary.productionEligible === summary.total ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-3164">
                {summary.productionEligible}
              </div>
              <div data-id="element-3165">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-3166">
                  Production Eligible
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-3167">
                  {Math.round(summary.productionEligible / summary.total * 100)}
                  % of questions
                </p>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3 border-slate-200" data-id="element-3168">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${summary.assessmentEligible === summary.total ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-3169">
                {summary.assessmentEligible}
              </div>
              <div data-id="element-3170">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-3171">
                  Assessment Eligible
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-3172">
                  {Math.round(summary.assessmentEligible / summary.total * 100)}
                  % of questions
                </p>
              </div>
            </Card>
          </div>

          {/* Score Distribution */}
          <Card className="p-5" data-id="element-3173">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4" data-id="element-3174">
              Score Distribution
            </h3>
            <div className="space-y-2" data-id="element-3175">
              {[{
            label: 'Excellent (90–100)',
            count: summary.scoreDistribution.excellent,
            color: 'bg-emerald-500'
          }, {
            label: 'Good (70–89)',
            count: summary.scoreDistribution.good,
            color: 'bg-blue-500'
          }, {
            label: 'Needs Work (50–69)',
            count: summary.scoreDistribution.needsWork,
            color: 'bg-amber-500'
          }, {
            label: 'Poor (< 50)',
            count: summary.scoreDistribution.poor,
            color: 'bg-rose-500'
          }].map(band => <div key={band.label} className="flex items-center gap-3" data-id="element-3176">
                  <div className="w-36 text-xs font-medium text-slate-600 shrink-0" data-id="element-3177">
                    {band.label}
                  </div>
                  <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden" data-id="element-3178">
                    <div className={`h-full ${band.color} rounded-full transition-all duration-700`} style={{
                width: `${summary.total > 0 ? band.count / summary.total * 100 : 0}%`
              }} data-id="element-3179" />
                  </div>
                  <div className="w-10 text-xs font-bold text-slate-700 text-right shrink-0" data-id="element-3180">
                    {band.count}
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Issues by Domain */}
          <Card className="p-5" data-id="element-3181">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4" data-id="element-3182">
              Issues by Domain
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-3183">
              {(Object.keys(DOMAIN_META) as ValidationIssue['domain'][]).map(domain => {
            const count = summary.byDomain[domain] ?? 0;
            const meta = DOMAIN_META[domain];
            const Icon = meta.icon;
            return <button key={domain} onClick={() => setDomainFilter(domainFilter === domain ? 'all' : domain)} className={`flex items-center gap-2 p-3 rounded-xl border transition-all text-left ${domainFilter === domain ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300'}`} data-id="element-3184">
                      <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} data-id="element-3185" />
                      <div className="min-w-0" data-id="element-3186">
                        <div className="text-xs font-bold text-slate-700 truncate" data-id="element-3187">
                          {meta.label}
                        </div>
                        <div className={`text-lg font-black ${count > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-id="element-3188">
                          {count}
                        </div>
                      </div>
                    </button>;
          })}
            </div>
          </Card>

          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-3" data-id="element-3189">
            <div className="flex items-center gap-2" data-id="element-3190">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-3191">
                Kind:
              </span>
              {(['all', 'blocker', 'coach', 'info'] as SeverityFilter[]).map(s => <button key={s} onClick={() => setSeverityFilter(s)} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${severityFilter === s ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3192">
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>)}
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer ml-auto" data-id="element-3193">
              <input type="checkbox" checked={showOnlyFailing} onChange={e => setShowOnlyFailing(e.target.checked)} className="rounded" data-id="element-3194" />
              Show only failing questions
            </label>
            <span className="text-xs text-slate-400" data-id="element-3195">
              {filteredResults.length} questions shown
            </span>
          </div>

          {/* Question Results */}
          <div className="space-y-2" data-id="element-3196">
            {filteredResults.length === 0 ? <Card className="p-8 text-center border-dashed border-2 border-emerald-200 bg-emerald-50/50" data-id="element-3197">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" data-id="element-3198" />
                <p className="font-bold text-emerald-800" data-id="element-3199">
                  All questions pass the selected filters!
                </p>
              </Card> : filteredResults.map(result => {
          const visibleIssues = (domainFilter === 'all' ? result.gate.issues : result.gate.issues.filter(i => i.domain === domainFilter)).filter(i => severityFilter === 'all' || i.kind === severityFilter);
          const isExpanded = expandedId === result.questionId;
          const blockerCount = visibleIssues.filter(i => i.kind === 'blocker').length;
          const coachCount = visibleIssues.filter(i => i.kind === 'coach').length;
          return <Card key={result.questionId} className={`overflow-hidden transition-all ${result.gate.verdict === 'FAIL' ? 'border-rose-200' : result.gate.verdict === 'REVIEW_NEEDED' ? 'border-amber-200' : ''}`} data-id="element-3200">
                    <button onClick={() => setExpandedId(isExpanded ? null : result.questionId)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors" data-id="element-3201">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-sm shrink-0 ${result.gate.score >= 90 ? 'bg-emerald-100 text-emerald-700' : result.gate.score >= 70 ? 'bg-blue-100 text-blue-700' : result.gate.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-3202">
                        {result.gate.score}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-3203">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-3204">
                          <span className="font-mono text-xs font-bold text-slate-700" data-id="element-3205">
                            {result.questionId}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-3206">
                            {result.familyId}
                          </span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${result.gate.verdict === 'PASS' ? 'bg-emerald-50 text-emerald-700' : result.gate.verdict === 'REVIEW_NEEDED' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`} data-id="element-3207">
                            {result.gate.verdict}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap" data-id="element-3208">
                          {blockerCount > 0 && <span className="flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full" data-id="element-3209">
                              <AlertOctagon className="w-3 h-3" data-id="element-3210" />
                              {blockerCount} blocker
                            </span>}
                          {coachCount > 0 && <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-3211">
                              <AlertTriangle className="w-3 h-3" data-id="element-3212" />
                              {coachCount} coach
                            </span>}
                          {visibleIssues.length === 0 && <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-3213">
                              <CheckCircle2 className="w-3 h-3" data-id="element-3214" />
                              Compliant
                            </span>}
                        </div>
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3215" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" data-id="element-3216" />}
                    </button>
                    {isExpanded && visibleIssues.length > 0 && <div className="border-t border-slate-100 divide-y divide-slate-50" data-id="element-3217">
                        {visibleIssues.map((issue, idx) => {
                const meta = DOMAIN_META[issue.domain];
                const Icon = meta.icon;
                return <div key={idx} className={`px-4 py-3 flex gap-3 ${issue.kind === 'blocker' ? 'bg-rose-50/50' : issue.kind === 'coach' ? 'bg-amber-50/50' : 'bg-slate-50/50'}`} data-id="element-3218">
                              <div className={`mt-0.5 shrink-0 ${issue.kind === 'blocker' ? 'text-rose-500' : issue.kind === 'coach' ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-3219">
                                {issue.kind === 'blocker' ? <AlertOctagon className="w-4 h-4" data-id="element-3220" /> : issue.kind === 'coach' ? <AlertTriangle className="w-4 h-4" data-id="element-3221" /> : <Info className="w-4 h-4" data-id="element-3222" />}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-3223">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5" data-id="element-3224">
                                  <span className={`text-xs font-bold ${meta.color}`} data-id="element-3225">
                                    <Icon className="w-3 h-3 inline mr-1" data-id="element-3226" />
                                    {meta.label}
                                  </span>
                                  <code className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded" data-id="element-3227">
                                    {issue.code}
                                  </code>
                                  <span className="text-[10px] text-slate-400" data-id="element-3228">
                                    sev:{issue.severity}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-700 leading-relaxed" data-id="element-3229">
                                  {issue.message}
                                </p>
                                {issue.suggestion && <p className="text-xs text-indigo-600 mt-1 italic" data-id="element-3230">
                                    💡 {issue.suggestion}
                                  </p>}
                              </div>
                            </div>;
              })}
                      </div>}
                  </Card>;
        })}
          </div>
        </>}
    </div>;
}
function SummaryCard({
  label,
  value,
  suffix,
  color,
  icon: Icon
}: {
  label: string;
  value: string;
  suffix?: string;
  color: 'emerald' | 'amber' | 'rose' | 'indigo';
  icon: ComponentType<{
    className?: string;
  }>;
}) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700'
  };
  return <Card className={`p-5 ${colors[color]} border`} data-id="element-3231">
      <div className="flex items-start justify-between" data-id="element-3232">
        <div data-id="element-3233">
          <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1" data-id="element-3234">
            {label}
          </p>
          <div className="flex items-baseline gap-1" data-id="element-3235">
            <span className="text-3xl font-black" data-id="element-3236">{value}</span>
            {suffix && <span className="text-sm font-medium opacity-60" data-id="element-3237">{suffix}</span>}
          </div>
        </div>
        <Icon className="w-5 h-5 opacity-50 mt-1" data-id="element-3238" />
      </div>
    </Card>;
}